// Generic imports
import axios from 'axios';
import _ from 'lodash';
import { evaluate } from 'mathjs';
import { migrateRateToLCL, migrateRateToAirFreight, migrateWMRatePairs, normalizeProductType } from './product.js';

// Custom imports
import currencyToText from '../../CurrencyToText';
import { formulaToParams, formulaToText } from '../../utils/Formula.js';
import { FieldTypes } from '../../FieldTypes.js';
import { calcFieldValue } from '../../utils/opportunity.js';

function getRatesString({ fieldBuyingRates, products, buyingRates }) {
    const rates = _.compact(fieldBuyingRates?.map(br => {
        // A link can outlive what it points to: the rate removed from the quote, or the charge
        // removed from the rate. Such a link has no text to give; reading through it crashed the
        // whole quote builder.
        const rate = (buyingRates || []).find(r => r.id === br.rateId);
        if (!rate) return null;
        const matchingProducts = (products || []).filter(s => s.type === rate.product?.type);
        if (_.isEmpty(matchingProducts) && rate.product?.type) return null;

        const field = _(rate.offer?.sections || []).map(s => s.offers?.[0]?.fields || []).flatten().find(f => f.id === br.fieldId);
        if (!field) return null;

        return field.values?.string?.value || '';
    }));

    return rates;
}

// A rate priced for one container type linked to a product of another type: the builder never
// applies it (getFieldRatesForProduct below), so it is not a cost of the quote either.
function rateTypeMismatch(rate, product) {
    return !!(rate?.product?.type && product?.type && normalizeProductType(rate.product.type) !== normalizeProductType(product.type));
}

// Removes every field link from a product to a rate priced for another container type. Changing a
// product's type keeps its id, so its links stay but no longer apply: the builder hid them while
// the Cost Center still showed them as used. The rates themselves stay in buyingRates, so they show
// in the Cost Center as not used, ready to add again. Returns the offers and what was unlinked.
function unlinkMismatchedRates({ offers, products, buyingRates }) {
    const productsById = _.keyBy(products || [], 'id');
    const ratesById = _.keyBy(buyingRates || [], 'id');
    const removed = [];
    const newOffers = (offers || []).map(offer => {
        let offerChanged = false;
        const sections = (offer.sections || []).map(section => ({
            ...section,
            offers: (section.offers || []).map(so => ({
                ...so,
                fields: (so.fields || []).map(field => {
                    if (!field.buyingRates?.length) return field;
                    const kept = field.buyingRates.filter(link => {
                        const product = productsById[link.opportunityProductId];
                        const rate = ratesById[link.rateId];
                        if (!product || !rate || !rateTypeMismatch(rate, product)) return true;
                        removed.push({ rate, product });
                        return false;
                    });
                    if (kept.length === field.buyingRates.length) return field;
                    offerChanged = true;
                    return { ...field, buyingRates: kept };
                }),
            })),
        }));
        return offerChanged ? { ...offer, sections } : offer;
    });
    return { offers: removed.length ? newOffers : offers, removed };
}

function getFieldRatesForProduct({ fieldBuyingRates, product, buyingRates, transportationMethod, allProducts, organization } ) {
    const rates = _.compact(fieldBuyingRates?.map(br => {
        if (br.opportunityProductId !== product.id) return null;
        const rate = buyingRates.find(r => r.id === br.rateId);
        if (!rate) return null;
        // A rate priced for a specific product type only applies to opportunity products
        // of that type. Changing a (sea) product's type keeps its id, so without this the
        // cost would wrongly re-point to the new type. Mirrors getRatesString's type check.
        if (rateTypeMismatch(rate, product)) return null;
        const field = _(rate.offer.sections).map(s => s.offers[0]?.fields).compact().flatten().find(f => f.id === br.fieldId);
        if (!field) return null;
        return {
            field,
            rate,
            rateProduct: rate.product
        };
    }));

    return _.compact(rates.map(r => {
        const fieldType = FieldTypes[transportationMethod].find(ft => ft.value === r.field.type);
        // Use the rate's densityRatio for buying cost W/M calculation (default 1000 if not set on rate)
        const rateDensityRatio = r.rateProduct?.densityRatio || 1000;
        const productsWithRateDensity = allProducts.map(p =>
            p.type === 'LCL' ? { ...p, densityRatio: rateDensityRatio } : p
        );
        const chargeLine = fieldType?.getChargeLines ? fieldType.getChargeLines({field: r.field, products: productsWithRateDensity, organization }) : null;
        if (!chargeLine) return null;
        // There may be no line to price this cost against at all - a field priced per
        // container has nothing to charge when there are no containers, which is exactly what
        // the uplift explainer asks for when it prices the field at zero quantity. The cost
        // contributes nothing rather than taking the page down.
        const line = chargeLine.find(cl => cl.productsInChargeLine.some(p => p.id === product.id)) || chargeLine[0];
        if (!line) return null;
        const { quantityMultiplier: multiplier, multiplierText } = line;
        return { field: r.field, rate: r.rate, multiplier, multiplierText, product, rateProduct: r.rateProduct };
    }));
}

function ratesToText({ rates, transportationMethod, exchangeRates, organization }) {
    let textSum = {};

    if (!rates) return [];
    if (_.isEmpty(rates)) return [];

    return [
        ...rates.map(r => {
            if (r.field.type === 'flat') {
                const value = r.field.values.flat;

                if (value.formula) {
                    const _sum = calcFieldValue({ field: r.field, products: [{ ...r.product, id: r.rateProduct.id }], transportationMethod, sections: r.rate.offer.sections, cost: 0, exchangeRates, lockedExchangeRates: {}, organization });
                    _.forEach(_sum.sum, (val, currency) => textSum[currency] = (textSum[currency] || 0) + (val || 0));
                    const sumValue = _sum.sum?.[value.currency] ?? 0;
                    // Say what the formula is, not that there is one. A cost reading
                    // "(formula) = $233" gives no way to find out why it is 233 — and when the
                    // reason is a minimum the formula carries, the amount on the cost can be set
                    // to zero and the charge will not move. formulaToText already puts these into
                    // words everywhere else; where it cannot, the old placeholder still stands.
                    const described = formulaToText({ formula: value.formula, errorValue: '(formula)', currency: value.currency, multiplierText: r.multiplierText, sections: r.rate.offer.sections, cost: 0 });
                    // A banded formula describes itself over several lines, which is right in a
                    // dialog and unreadable in a one-line cost. Flattened and cut short — the
                    // minimum leads, so the part that explains a cost refusing to move is what
                    // survives the trim.
                    const oneLine = String(described || '').replace(/\s+/g, ' ').trim();
                    const summary = oneLine.length > 80 ? `${oneLine.slice(0, 79)}…` : oneLine;
                    return `${r.field.title ? `${r.field.title}: ` : ''}${summary || '(formula)'} = ${currencyToText(value.currency, true, sumValue.toLocaleString())}`;
                } else {
                    const valueNum = parseFloat(value.value) || 0;
                    textSum[value.currency] = (textSum[value.currency] || 0) + valueNum;
                    return `${r.field.title ? `${r.field.title}: ` : ''}${currencyToText(value.currency, true, valueNum.toLocaleString())} / shipment`;
                }
            } else {
                // Prefer the value keyed by the rate's own product id, since other keys may be
                // placeholder entries with default (USD) currency that don't reflect the rate's currency.
                const value = r.field.values?.[r.rateProduct?.id] || _.values(r.field.values).find(v => v?.currency && (v.value != null || v.formula)) || _.values(r.field.values)[0];
                if (!value) return;

                const multiplierFormatted = (r.multiplier ?? 1).toLocaleString({maximumFractionDigits: 2});

                if (value.formula) {

                    const _sum = calcFieldValue({ 
                        field: r.field, 
                        products: [{ ...r.product, id: r.rateProduct.id }], 
                        transportationMethod, 
                        sections: r.rate.offer.sections, 
                        cost: 0, 
                        exchangeRates, 
                        lockedExchangeRates: {}, 
                        organization, 
                        forceLine: {
                            ...r,
                            quantityMultiplier: r.multiplier,
                            fieldValueId: r.rateProduct.id
                        }
                    });

                    const text = (() => {
                        const attempt = formulaToParams({ formula: value.formula });
                        if (!attempt) {
                            const formulaStr = formulaToText({ formula: value.formula, errorValue: 'formula', currency: value.currency, multiplierText: r.multiplierText, sections: r.field.sections, cost: 0 });
                            return `${formulaStr} calculated for ${multiplierFormatted} ${r.multiplierText}`;
                        }
                        if (attempt.tiers) {
                            if (_sum.sum?.[value.currency] === attempt.minimum) return `minimum (for ${multiplierFormatted} ${r.multiplierText})`;
                            const mult = r.multiplier ?? 1;
                            const money = v => currencyToText(value.currency, true, (v ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 }));
                            if (attempt.acc) {
                                // Accumulative tiers stack like tax brackets: each band's rate applies
                                // only to the portion of the amount inside that band. Show the per-band
                                // breakdown so the number reconciles — a single "rate × total" would be
                                // misleading (e.g. reads as 66 × 97 but the total is the stacked sum).
                                const parts = [];
                                let prev = 0;
                                for (const t of attempt.tiers) {
                                    if (mult <= prev) break;
                                    const upper = t.step ?? Number.MAX_VALUE;
                                    if (t.type === 'flat') {
                                        parts.push(`${money(t.value)} flat`);
                                    } else {
                                        const units = Math.min(mult, upper) - prev;
                                        parts.push(`${units.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${r.multiplierText} × ${money(t.value)}`);
                                    }
                                    prev = upper;
                                }
                                return parts.join(' + ');
                            }
                            // Non-accumulative: the whole amount is charged at the single band it falls into.
                            const tier = attempt.tiers.find(t => mult < (t.step ?? (Number.MAX_VALUE)));
                            const prevTier = attempt.tiers.indexOf(tier) > 0 ? attempt.tiers[attempt.tiers.indexOf(tier) - 1] : null;
                            const tierValue = tier?.value ?? 0;
                            return `${money(tierValue)} (${prevTier ? prevTier.step : 0}${r.multiplierText}${tier.step ? ` - ${tier.step}${r.multiplierText}` : '<'}) × ${multiplierFormatted} ${r.multiplierText}`;
                        }
                        const formulaStr = formulaToText({ formula: value.formula, errorValue: 'formula', currency: value.currency, multiplierText: r.multiplierText, sections: r.field.sections, cost: 0 });
                        return `${formulaStr} calculated for ${multiplierFormatted} ${r.multiplierText}`;
                    })();
                    _.forEach(_sum.sum, (val, currency) => textSum[currency] = (textSum[currency] || 0) + (val || 0));
                    const sumValue = _sum.sum?.[value.currency] ?? 0;
                    return `${r.field.title ? `${r.field.title}: ` : ''}${text} = ${currencyToText(value.currency, true, sumValue.toLocaleString({maximumFractionDigits: 2}))}`;
                } else {
                    const valueNum = parseFloat(value.value) || 0;
                    const totalValue = valueNum * (r.multiplier ?? 1);
                    textSum[value.currency] = (textSum[value.currency] || 0) + totalValue;
                    return `${r.field.title ? `${r.field.title}: ` : ''}${currencyToText(value.currency, true, valueNum.toLocaleString())} × ${multiplierFormatted} ${r.multiplierText} = ${currencyToText(value.currency, true, totalValue.toLocaleString())}`;
                }
            }
            return '';
        }),
        `<b>Total:</b> ` + _.map(textSum, (val, currency) => currencyToText(currency, true, (val || 0).toLocaleString())).join(', ')
    ].map(t => `<div>${t}</div>`);
}

function getSum({ rates, exchangeRates, transportationMethod, organization }) {
    let sum = {};

    rates.forEach(r => {
        if (r.field?.type === 'string') return;
        // Same as calcFieldValue: a cost outliving the rate it came from must not take the page
        // with it. See the note there.
        if (!r.rate?.offer?.sections || !r.rateProduct) return;
        const _sum = calcFieldValue({
            field: r.field,
            products: [{ ...r.product, id: r.rateProduct.id }],
            transportationMethod,
            sections: r.rate.offer.sections,
            cost: 0,
            exchangeRates,
            lockedExchangeRates: {},
            organization,
            forceLine: { ...r, quantityMultiplier: r.multiplier, fieldValueId: r.field.type === 'flat' ? 'flat' : r.rateProduct.id }
        });
        _.forEach(_sum.sum, (val, currency) => sum[currency] = (sum[currency] || 0) + val);
    });

    return sum;
}

function getTotalRates({ field, fieldBuyingRates, products, buyingRates, transportationMethod, exchangeRates, skipSumCurrencies, organization }) {
    let sums = {};
    const fieldType = FieldTypes[transportationMethod]?.find(ft => ft.value === field.type);
    if (!fieldType?.getChargeLines) return null;
    const chargeLines = fieldType.getChargeLines({ field, products, buyingRates, fieldBuyingRates, organization }) || [];

    chargeLines.forEach(cl => {
        const rates = cl.buyingRates || [];
        const tempSums = getSum({ rates, exchangeRates, transportationMethod, allProducts: products, organization });
        _.forEach(tempSums, (val, currency) => sums[currency] = (sums[currency] || 0) + val);
    });

    if (skipSumCurrencies) return sums;
    return _.sum(_.map(sums, (val, currency) => val / exchangeRates[currency])) || 0;
}

function getOverlappingRates({ rates }) {
    function textToValue(text, _default) {
        if (!text) return _default;
        return new Date(text).getTime() / 1000 / 60 / 60;
    }

    const supplierRates = rates.filter(r => !r.deleted && r.supplier?.organization && r.product);
    const groups = _.groupBy(supplierRates, r => `${r.supplier.organization}-${r.product.type}-${r.product.dangerous ? 'yes' : 'no'}-${r.transportationMethod}-${r.source?.type || ''}-${r.source?.id || ''}-${r.destination?.type || ''}-${r.destination?.id || ''}-${r.offer?.transshipment || ''}`);
    const overlappingRoutes = _(groups).values().filter(rates => {
        const parsed = _(rates).map(r => ({ from: textToValue(r.offer.validFrom, 0), until: textToValue(r.offer.validUntil, 1139566), rate: r })).sortBy(r => r.from).value();
        const filtered = _.filter(parsed, (r, i) => _.some(parsed, (r2, i2) => (i !== i2 && ((r.from <= r2.until && r.until >= r2.from) || (r2.from < r.until && r2.until > r.from)))));
        return !_.isEmpty(filtered);
    }).value();

    return overlappingRoutes;
}

async function getRatesFromServer({ mode = 'rates', accesstoken, organizationId, forceReset, onProgress }) {
    let db = null;
    
    // Open the database with error handling
    try {
        db = await new Promise((res, rej) => {
            const request = indexedDB.open(`${organizationId}-${mode}`, 1);
            request.onerror = (event) => {
                console.error('[Rates: getRatesFromServer] Why didn\'t you allow my web app to use IndexedDB?!', event);
                rej(event.target.error);
            };
            request.onsuccess = (event) => {
                console.log('Database opened successfully.');
                res(event.target.result);
            };
            request.onupgradeneeded = (event) => {
                console.log('Database onupgradeneeded called.');
                const db = event.target.result;
                try {
                    db.createObjectStore(mode, { keyPath: 'id' });
                } catch (e) {
                    console.warn('[Rates: getRatesFromServer] Object store might already exist:', e);
                }
            };
        });
    } catch (e) {
        console.warn('[Rates: getRatesFromServer] Failed to open IndexedDB, will continue without cache:', e?.message || e);
        // Continue without IndexedDB - will fetch from API only
        db = null;
    }

    // Get the items from the database
    let cache = [];
    if (db && !forceReset) {
        try {
            cache = await new Promise((res, rej) => {
                console.log('Reading from IndexedDB.', new Date().getTime());
                const transaction = db.transaction([mode], 'readonly');
                const store = transaction.objectStore(mode);
                
                // Add transaction-level error handling
                transaction.onerror = (event) => {
                    console.error('[Rates: getRatesFromServer] Transaction error reading from IndexedDB:', event.target.error);
                    rej(event.target.error);
                };
                transaction.onabort = (event) => {
                    console.error('[Rates: getRatesFromServer] Transaction aborted reading from IndexedDB:', event.target.error);
                    rej(event.target.error || new Error('Transaction aborted'));
                };
                
                const request = store.getAll();
                request.onsuccess = (event) => {
                    console.log('Done reading', event.target.result.length, ' from IndexedDB.', new Date().getTime());
                    res(event.target.result);
                };
                request.onerror = (event) => {
                    console.error('[Rates: getRatesFromServer] Request error reading from IndexedDB:', event.target.error);
                    rej(event.target.error);
                }
            });
        } catch (e) {
            // If IndexedDB read fails, return empty array and continue with API fetch
            console.warn('[Rates: getRatesFromServer] Failed to read from IndexedDB, continuing with API fetch:', e?.message || e);
            cache = [];
        }
    }

    const latest = (_.max(cache.map(r => parseInt(r.modified))) || 0) + 1;

    // Get the items from the API
    const items = [];
    let cursor = null;
    let shouldResetLocalDB = false;
    console.log(`[Rates: getRatesFromServer] Starting /api/${mode}?organizationId=${organizationId}&since=${latest}`);
    do {
        const { data } = await axios(`/api/${mode}?organizationId=${organizationId}&accesstoken=${accesstoken}&since=${latest}&cursor=${cursor ? cursor : ''}`);
        if (_.isArray(data)) return data;
        cursor = data.cursor;
        shouldResetLocalDB = data.shouldResetLocalDB;
        items.push(...data.rates);
        console.log(`[Rates: getRatesFromServer] Read ${items.length} items. Has more? ${cursor ? 'Yes' : 'No'}`);
        if (onProgress) onProgress({ total: data.count, count: items.length });
    } while (cursor);

    // Clear IndexexDB if requested
    if (shouldResetLocalDB && db) {
        console.log('[Rates: getRatesFromServer] Reset requested.', latest < new Date(2025, 4, 10).getTime());

        // Clear IndexedDB
        try {
            const transaction = db.transaction([mode], 'readwrite');
            const store = transaction.objectStore(mode);
            
            // Add transaction-level error handling
            transaction.onerror = (event) => {
                console.error('[Rates: getRatesFromServer] Transaction error clearing IndexedDB:', event.target.error);
            };
            transaction.onabort = (event) => {
                console.error('[Rates: getRatesFromServer] Transaction aborted clearing IndexedDB:', event.target.error);
            };
            
            await new Promise((res, rej) => {
                const request = store.clear();
                request.onsuccess = (event) => {
                    console.log('[Rates: getRatesFromServer] IndexedDB cleared.');
                    res();
                };
                request.onerror = (event) => {
                    console.error('[Rates: getRatesFromServer] Error clearing IndexedDB.', event.target.error);
                    rej(event.target.error);
                }
            });
        } catch (e) {
            // Log the error but continue - clearing is not critical
            console.warn('[Rates: getRatesFromServer] Failed to clear IndexedDB, continuing:', e?.message || e);
        }
    }

    // Saving to IndexedDB
    if (db && items.length > 0) {
        try {
            const transaction = db.transaction([mode], 'readwrite');
            const store = transaction.objectStore(mode);
            
            // Add transaction-level error handling
            transaction.onerror = (event) => {
                console.error('[Rates: getRatesFromServer] Transaction error saving to IndexedDB:', event.target.error);
            };
            transaction.onabort = (event) => {
                console.error('[Rates: getRatesFromServer] Transaction aborted saving to IndexedDB:', event.target.error);
            };
            
            console.log('[Rates: getRatesFromServer] Adding', items.length, `${mode} to IndexedDB.`, new Date().getTime());
            
            // Track failed saves
            let failedCount = 0;
            const failedErrors = [];
            
            for (let i = 0; i < items.length; i++) {
                const r = items[i];
                try {
                    await new Promise((res, rej) => {
                        const request = store.put(r); // TODO: Check if exists and update
                        request.onsuccess = (event) => {
                            res();
                        };
                        request.onerror = (event) => {
                            rej(event.target.error);
                        }
                    });
                } catch (e) {
                    failedCount++;
                    // Only log first few errors to avoid spam
                    if (failedCount <= 5) {
                        failedErrors.push(e?.message || e?.toString() || 'Unknown error');
                        console.warn(`[Rates: getRatesFromServer] Failed to save item ${i} to IndexedDB:`, e?.message || e);
                    }
                    // Check for quota exceeded error - this is a critical error
                    if (e?.name === 'QuotaExceededError' || e?.message?.includes('quota') || e?.message?.includes('QuotaExceeded')) {
                        console.error('[Rates: getRatesFromServer] IndexedDB quota exceeded. Stopping save operation.');
                        break; // Stop trying to save more items
                    }
                }
            }
            
            if (failedCount > 0) {
                console.warn(`[Rates: getRatesFromServer] Failed to save ${failedCount} out of ${items.length} items to IndexedDB.`, 
                    failedCount <= 5 ? `Errors: ${failedErrors.join('; ')}` : '');
            }
            
            console.log('[Rates: getRatesFromServer] Done', items.length, `${mode} to IndexedDB.`, new Date().getTime());
        } catch (e) {
            // If saving to IndexedDB fails completely, log but continue
            console.warn('[Rates: getRatesFromServer] Failed to save to IndexedDB, but continuing with API data:', e?.message || e);
        }
    }

    console.log('[Rates: getRatesFromServer] Done', items.length, `${mode} to IndexedDB.`, new Date().getTime());

    if (shouldResetLocalDB) {
        return migrateWMRatePairs(items.filter(r => r.id !== `${organizationId}-reset`).map(r => migrateRateToAirFreight(migrateRateToLCL(r))));

    } else {
        return migrateWMRatePairs(_.uniqBy([...items, ...cache], 'id').filter(r => r.id !== `${organizationId}-reset`).map(r => migrateRateToAirFreight(migrateRateToLCL(r))));
    }
}

/**
 * Converts a buying rate from KG to LBS if needed
 * @param {Object} rate - The buying rate to potentially convert
 * @param {Array} opportunityProducts - The products in the opportunity
 * @returns {Object} - The converted rate (or original if no conversion needed)
 */
function convertRateKgToLbsIfNeeded({ rate, opportunityProducts }) {
    // Check if opportunity products are in LBS
    // Need at least one product with kgOrLbs set to 'lbs', and all products with kgOrLbs set must be 'lbs'
    const opportunityProductsInLbs = opportunityProducts && opportunityProducts.length > 0 && 
        opportunityProducts.some(p => p.kgOrLbs === 'lbs') &&
        opportunityProducts.every(p => !p.kgOrLbs || p.kgOrLbs === 'lbs');
    
    // Check if rate product is in KG
    const rateProductInKg = rate?.product?.kgOrLbs === 'kg';
    
    // If both conditions are met, convert the rate
    if (!opportunityProductsInLbs || !rateProductInKg || !rate) {
        return rate;
    }
    
    // Clone the rate to avoid mutating the original
    const convertedRate = _.cloneDeep(rate);
    
    // Conversion factor: 1 kg = 2.20462 lbs
    const KG_TO_LBS = 2.20462;
    
    // Convert the rate product's weight unit
    convertedRate.product.kgOrLbs = 'lbs';
    
    // Convert the rate product's weight value if it exists
    if (convertedRate.product.weight) {
        convertedRate.product.weight = convertedRate.product.weight * KG_TO_LBS;
    }
    
    // Convert all numeric values in rate fields
    // For per-unit rates, we need to divide by the conversion factor (price per kg -> price per lb)
    // For flat rates, we typically don't convert, but let's convert per-unit values
    convertedRate.offer.sections.forEach(section => {
        section.offers.forEach(offer => {
            offer.fields.forEach(field => {
                if (!field.values) return;
                
                // Convert flat field values
                if (field.values.flat && field.values.flat.value !== null && field.values.flat.value !== undefined) {
                    if (typeof field.values.flat.value === 'number') {
                        // For flat rates, typically no conversion needed, but if it's weight-based, convert
                        // Actually, flat rates are per shipment, so no conversion needed
                    }
                }
                
                // Convert per-unit field values (these are weight-based)
                Object.keys(field.values).forEach(key => {
                    const valueObj = field.values[key];
                    if (valueObj && typeof valueObj.value === 'number' && valueObj.value !== null && valueObj.value !== undefined) {
                        // For per-unit rates (per kg), convert to per lb by dividing
                        // If it's $X per kg, it becomes $X/2.20462 per lb
                        if (field.type === 'per-unit' || field.type === 'per-unit-type' || field.type === 'per-unit-type-weight') {
                            valueObj.value = valueObj.value / KG_TO_LBS;
                        }
                    }
                });
            });
        });
    });
    
    return convertedRate;
}

export { ratesToText, getRatesString, getTotalRates, getOverlappingRates, getRatesFromServer, getFieldRatesForProduct, convertRateKgToLbsIfNeeded, rateTypeMismatch, unlinkMismatchedRates };
